import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Check, X, Search, Trash2, Euro, FileDown, CalendarDays, Eye, FileText } from "lucide-react";
import { tr } from "../contexts/LangContext";

/* ====================== Shared Stage Registrations panel ======================
 * RBAC behavior (RYCM iter 22) — auto-derived from the connected user role(s):
 *   - admin           → full control (status edit, delete, payment toggle, financials)
 *   - secretary       → status edit + delete + export (NO payment toggle, NO financials)
 *   - treasurer       → payment toggle + financials (NO status edit, NO delete)
 *   - president       → READ-ONLY
 * Used in: /admin (via AdminExtensions), /secretariat, /treasury, /president.
 * ============================================================================== */
export default function StageRegistrationsPanel({ embedded = false }) {
  const { user } = useAuth();
  const userRoles = useMemo(() => {
    const r = new Set();
    if (user?.role) r.add(user.role);
    (user?.roles || []).forEach((x) => r.add(x));
    return r;
  }, [user]);
  const canEditStatus = userRoles.has("admin") || userRoles.has("secretary");
  const canDelete     = userRoles.has("admin") || userRoles.has("secretary");
  const canTogglePay  = userRoles.has("admin") || userRoles.has("treasurer");
  const canFinancials = userRoles.has("admin") || userRoles.has("treasurer");

  const [regs, setRegs] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all"); // stage_id
  const [ageFilter, setAgeFilter]     = useState("all"); // jeunes | adultes
  const [groupByStage, setGroupByStage] = useState(true);
  const [financials, setFinancials] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fiche, setFiche] = useState(null); // registration object opened in modal
  const [allStages, setAllStages] = useState([]); // informational stages (category)
  const [pendingOrders, setPendingOrders] = useState([]); // stage orders not yet paid

  const load = async () => {
    try {
      const [regsR, stagesR, ordersR] = await Promise.all([
        api.get("/admin/stage-registrations"),
        api.get("/stages").catch(() => ({ data: [] })),
        api.get("/admin/orders").catch(() => ({ data: [] })),
      ]);
      setRegs(regsR.data || []);
      setAllStages(stagesR.data || []);
      // Pending stage orders (status != paid) — these have no entry yet in stage_registrations
      const pend = [];
      (ordersR.data || []).forEach((o) => {
        if ((o.status || "pending") === "paid") return;
        (o.items || []).forEach((it) => {
          if (it.type !== "stage") return;
          pend.push({ ...it, _order: o });
        });
      });
      setPendingOrders(pend);
      if (canFinancials) {
        try {
          const f = await api.get("/admin/stage-registrations/financials");
          setFinancials(f.data);
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  // Build a list of distinct stage products from registrations themselves
  // (the registrations point at `products` collection, not the informational /stages).
  const stageOptions = useMemo(() => {
    const map = new Map();
    regs.forEach((r) => { if (r.stage_id) map.set(r.stage_id, r.stage_title || r.stage_id); });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title })).sort((a, b) => a.title.localeCompare(b.title));
  }, [regs]);

  // Map stage_title → age category (from informational stages list — best-effort)
  const ageByTitle = useMemo(() => {
    const m = {};
    allStages.forEach((s) => {
      ["title_fr", "title_nl", "title_en"].forEach((k) => {
        if (s[k]) m[s[k].toLowerCase()] = s.category;
      });
    });
    return m;
  }, [allStages]);

  const guessAge = (r) => {
    const t = (r.stage_title || "").toLowerCase();
    const fromMap = ageByTitle[t];
    if (fromMap) return fromMap;
    // Heuristic fallback based on title keywords
    if (/(adulte|adults?)/i.test(t)) return "adultes";
    if (/(jeune|kid|enfant|ado|young|7-12|13-16)/i.test(t)) return "jeunes";
    return "autre";
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return regs.filter((r) => {
      if (statusFilter !== "all" && (r.status || "pending") !== statusFilter) return false;
      if (stageFilter !== "all" && r.stage_id !== stageFilter) return false;
      if (ageFilter !== "all" && guessAge(r) !== ageFilter) return false;
      if (!q) return true;
      const hay = [
        r.participant_name, r.participant_first_name, r.participant_last_name,
        r.stage_title, r.parent_email, r.participant_email, r.participant_phone,
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [regs, search, statusFilter, stageFilter, ageFilter, ageByTitle]);

  const updateStatus = async (r, status) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("status", status);
      await api.patch(`/admin/stage-registrations/${r.id}`, fd);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const togglePaid = async (r) => {
    if (!canTogglePay) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("paid", String(!r.paid));
      fd.append("payment_method", r.payment_method || "");
      await api.patch(`/admin/stage-registrations/${r.id}/payment`, fd);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!canDelete) return;
    if (!window.confirm(`${tr("Supprimer l'inscription de")} ${r.participant_name || r.participant_first_name || ""} ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/stage-registrations/${r.id}`);
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const exportCsv = (productId) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/admin/stage-products/${productId}/export.csv`, "_blank");
  };

  const exportAllXlsx = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    // Pass current filters server-side (stage_id, status). Age filter is client-side only.
    const params = new URLSearchParams();
    if (stageFilter !== "all") params.set("stage_id", stageFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    const qs = params.toString();
    window.open(`${backend}/api/admin/stage-registrations/export.xlsx${qs ? "?" + qs : ""}`, "_blank");
  };

  const downloadFichePdf = (regId) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/admin/stage-registrations/${regId}/fiche.pdf`, "_blank");
  };

  // Group by stage_title
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const k = r.stage_title || "Sans stage";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const STATUS_OPTS = ["pending", "confirmed", "waitlist", "cancelled", "rejected"];
  const statusBadge = (s) => ({
    confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    pending:   "bg-amber-100   text-amber-800   border-amber-200",
    waitlist:  "bg-sky-100     text-sky-800     border-sky-200",
    cancelled: "bg-slate-100   text-slate-700   border-slate-200",
    rejected:  "bg-red-100     text-red-800     border-red-200",
  }[s || "pending"]);

  return (
    <div className={embedded ? "" : "pt-24 md:pt-28 pb-16 min-h-screen bg-sand"} data-testid="stage-registrations-panel">
      <div className={embedded ? "" : "max-w-7xl mx-auto px-4 md:px-10 lg:px-12"}>
        {!embedded && (
          <>
            <div className="label-eyebrow text-club-red mb-3">{tr("Inscriptions aux stages")}</div>
            <h1 className="font-serif text-4xl text-navy">{tr("Inscriptions stages")}</h1>
            <div className="divider-gold mt-5 mb-8" />
          </>
        )}

        {/* Financial summary (treasurer & admin) */}
        {canFinancials && financials && (
          <div className="mb-6 bg-white border border-slate-200 p-5" data-testid="stage-financials">
            <div className="flex items-center gap-2 mb-4">
              <Euro className="w-4 h-4 text-club-yellow-dark" />
              <h3 className="font-serif text-lg text-navy">{tr("Synthèse financière par stage")}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2">{tr("Stage")}</th>
                    <th className="px-2">{tr("PU")}</th>
                    <th className="px-2">{tr("Inscriptions")}</th>
                    <th className="px-2">{tr("Payés")}</th>
                    <th className="px-2 text-right">{tr("Attendu")}</th>
                    <th className="px-2 text-right">{tr("Encaissé")}</th>
                  </tr>
                </thead>
                <tbody>
                  {financials.rows.map((r) => (
                    <tr key={r.stage_id} className="border-b border-slate-100" data-testid={`fin-row-${r.stage_id}`}>
                      <td className="py-2 pr-2 text-navy">{r.stage_title}</td>
                      <td className="px-2">€ {Number(r.unit_price).toFixed(2)}</td>
                      <td className="px-2">{r.registrations}</td>
                      <td className="px-2">{r.paid_count}</td>
                      <td className="px-2 text-right">€ {Number(r.expected).toFixed(2)}</td>
                      <td className="px-2 text-right font-semibold text-emerald-700">€ {Number(r.received).toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-sand" data-testid="fin-totals">
                    <td className="py-2 pr-2 text-navy">TOTAL</td>
                    <td className="px-2"></td>
                    <td className="px-2">{financials.totals.registrations}</td>
                    <td className="px-2">{financials.totals.paid_count}</td>
                    <td className="px-2 text-right">€ {Number(financials.totals.expected).toFixed(2)}</td>
                    <td className="px-2 text-right text-emerald-700">€ {Number(financials.totals.received).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pending stage orders alert (RYCM iter 26) — these are checkouts (virement bancaire)
            not yet confirmed as paid, so they have no registration yet. */}
        {pendingOrders.length > 0 && (
          <div className="mb-4 bg-amber-50 border border-amber-300 p-4" data-testid="pending-stage-orders">
            <div className="flex items-center justify-between mb-2">
              <div className="font-serif text-navy text-base">
                ⚠️ {pendingOrders.length} {pendingOrders.length > 1 ? tr("commandes stage en attente de paiement") : tr("commande stage en attente de paiement")}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-amber-800">{tr("Marquer payé pour finaliser l'inscription")}</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-600 border-b border-amber-300">
                  <th className="py-2 pr-2">{tr("Commande")}</th>
                  <th className="px-2">{tr("Client")}</th>
                  <th className="px-2">{tr("Stage")}</th>
                  <th className="px-2">{tr("Méthode")}</th>
                  <th className="px-2 text-right">{tr("Montant")}</th>
                  {canTogglePay && <th className="px-2">{tr("Action")}</th>}
                </tr>
              </thead>
              <tbody>
                {pendingOrders.map((it) => {
                  const o = it._order;
                  return (
                    <tr key={`${o.id}-${it.product_id}`} className="border-b border-amber-100" data-testid={`pending-order-${o.id}`}>
                      <td className="py-2 pr-2">
                        <div className="font-mono text-[11px] text-navy">{o.id.slice(0, 8)}</div>
                        <div className="text-[10px] text-slate-500">{(o.created_at || "").slice(0, 10)}</div>
                      </td>
                      <td className="px-2">
                        <div className="text-navy">{o.customer_name}</div>
                        <div className="text-[11px] text-slate-500">{o.customer_email}</div>
                      </td>
                      <td className="px-2 text-slate-700">{it.name}</td>
                      <td className="px-2 text-[11px] uppercase tracking-widest text-slate-600">{o.payment_method}</td>
                      <td className="px-2 text-right font-semibold">€ {Number(it.line_total || 0).toFixed(2)}</td>
                      {canTogglePay && (
                        <td className="px-2">
                          <button
                            disabled={busy}
                            onClick={async () => {
                              if (!window.confirm(`${tr("Marquer la commande de")} ${o.customer_name} ${tr("comme payée ?")}\n${tr("Cela créera l'inscription et enverra la fiche par e-mail.")}`)) return;
                              setBusy(true);
                              try {
                                await api.post(`/admin/orders/${o.id}/mark-paid`);
                                await load();
                              } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
                              finally { setBusy(false); }
                            }}
                            className="text-[11px] uppercase tracking-widest text-emerald-700 border border-emerald-700 px-3 py-1.5 hover:bg-emerald-700 hover:text-white"
                            data-testid={`pending-markpaid-${o.id}`}
                          >{tr("Marquer payé")}</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="stage-regs-toolbar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              placeholder={tr("Rechercher (nom, e-mail, téléphone, stage…)")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full border border-slate-300 px-3 py-2 outline-none focus:border-navy"
              data-testid="stage-regs-search"
            />
          </div>
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            className="border border-slate-300 px-3 py-2 text-sm max-w-[260px]" data-testid="stage-regs-stage-filter">
            <option value="all">{tr("Tous les stages")} ({regs.length})</option>
            {stageOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.title} ({regs.filter((r) => r.stage_id === o.id).length})</option>
            ))}
          </select>
          <select value={ageFilter} onChange={(e) => setAgeFilter(e.target.value)}
            className="border border-slate-300 px-3 py-2 text-sm" data-testid="stage-regs-age-filter">
            <option value="all">{tr("Tous âges")}</option>
            <option value="jeunes">Jeunes</option>
            <option value="adultes">Adultes</option>
            <option value="autre">{tr("Autre")}</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-300 px-3 py-2 text-sm" data-testid="stage-regs-status-filter">
            <option value="all">Tous statuts ({regs.length})</option>
            {STATUS_OPTS.map((s) => (
              <option key={s} value={s}>{s} ({regs.filter((r) => (r.status || "pending") === s).length})</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setGroupByStage((v) => !v)}
            className={`px-3 py-2 text-xs uppercase tracking-widest border ${groupByStage ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}
            data-testid="stage-regs-group-toggle"
            title={groupByStage ? tr("Voir en liste plate") : tr("Grouper par stage")}
          >
            {groupByStage ? tr("Vue groupée") : tr("Vue liste")}
          </button>
          <button
            type="button"
            onClick={exportAllXlsx}
            className="px-3 py-2 text-xs uppercase tracking-widest border border-club-yellow-dark bg-club-yellow text-navy hover:bg-club-yellow-dark"
            data-testid="stage-regs-export-all"
            title={tr("Exporter la sélection courante en XLSX (1 feuille par stage)")}
          >
            <FileDown className="w-3 h-3 inline mr-1" /> Exporter ({filtered.length}) XLSX
          </button>
        </div>

        {/* Table (single or grouped by stage) */}
        {groupByStage ? (
          <div className="space-y-6" data-testid="stage-regs-grouped">
            {groups.length === 0 && (
              <div className="bg-white border border-slate-200 text-center text-slate-500 py-8" data-testid="stage-regs-empty">{tr("Aucune inscription pour les filtres actuels.")}</div>
            )}
            {groups.map(([title, rs]) => (
              <div key={title} className="bg-white border border-slate-200" data-testid={`stage-group-${title}`}>
                <div className="px-4 py-3 bg-sand border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="font-serif text-navy text-base">{title}</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">
                      {rs.length} {rs.length > 1 ? tr("inscriptions") : tr("inscription")}
                      {" · "}
                      {rs.filter((r) => r.paid).length} {rs.filter((r) => r.paid).length > 1 ? tr("payées") : tr("payée")}
                    </div>
                  </div>
                  {rs[0]?.stage_id && (
                    <button
                      onClick={() => exportCsv(rs[0].stage_id)}
                      className="text-[11px] uppercase tracking-widest text-navy border border-navy px-3 py-1.5 hover:bg-navy hover:text-white"
                      data-testid={`stage-group-export-${rs[0].stage_id}`}
                    >
                      <FileDown className="w-3 h-3 inline mr-1" /> CSV
                    </button>
                  )}
                </div>
                <RegistrationsTable
                  rows={rs} canEditStatus={canEditStatus} canTogglePay={canTogglePay} canDelete={canDelete}
                  STATUS_OPTS={STATUS_OPTS} statusBadge={statusBadge} busy={busy}
                  updateStatus={updateStatus} togglePaid={togglePaid} del={del} exportCsv={exportCsv}
                  onOpenFiche={setFiche} downloadFichePdf={downloadFichePdf}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="stage-regs-table">
            <RegistrationsTable
              rows={filtered} canEditStatus={canEditStatus} canTogglePay={canTogglePay} canDelete={canDelete}
              STATUS_OPTS={STATUS_OPTS} statusBadge={statusBadge} busy={busy}
              updateStatus={updateStatus} togglePaid={togglePaid} del={del} exportCsv={exportCsv}
              onOpenFiche={setFiche} downloadFichePdf={downloadFichePdf}
            />
          </div>
        )}
        <div className="text-[11px] text-slate-500 mt-3">
          {filtered.length} {tr("sur")} {regs.length} {tr("inscription(s).")}
          {!canEditStatus && !canTogglePay && ` ${tr("— Lecture seule.")}`}
        </div>
      </div>
      {fiche && (
        <FicheModal
          reg={fiche}
          canEdit={canEditStatus}
          onClose={() => setFiche(null)}
          onSaved={async () => { await load(); }}
          downloadPdf={() => downloadFichePdf(fiche.id)}
        />
      )}
    </div>
  );
}

function FicheModal({ reg, canEdit, onClose, onSaved, downloadPdf }) {
  const [form, setForm] = useState({
    participant_first_name: reg.participant_first_name || "",
    participant_last_name:  reg.participant_last_name  || "",
    participant_birthdate:  reg.participant_birthdate  || "",
    participant_email:      reg.participant_email      || reg.parent_email || "",
    participant_phone:      reg.participant_phone      || reg.parent_phone || "",
    participant_address:    reg.participant_address    || "",
    participant_weight:     reg.participant_weight     || "",
    participant_height:     reg.participant_height     || "",
    participant_boat:       reg.participant_boat       || "",
    participant_experience: reg.participant_experience || reg.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/stage-registrations/${reg.id}/fiche`, form);
      await onSaved();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const F = ({ label, value, onChange, type = "text", testid }) => (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      {canEdit ? (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-navy bg-white" data-testid={testid} />
      ) : (
        <div className="text-sm text-navy py-1.5">{value || "—"}</div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="fiche-modal">
      <div className="bg-white max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="bg-club-red text-white px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-80">{tr("Fiche d'inscription")} · Réf {reg.id.slice(0, 8)}</div>
            <div className="font-serif text-xl">{reg.stage_title || "—"}</div>
          </div>
          <button onClick={onClose} className="hover:opacity-70" data-testid="fiche-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status + payment chips */}
          <div className="flex gap-2 flex-wrap text-[11px]">
            <span className="bg-sand text-navy uppercase tracking-widest px-2 py-1 border border-slate-200">
              Statut : {reg.status || "pending"}
            </span>
            <span className={`uppercase tracking-widest px-2 py-1 border ${reg.paid ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
              {reg.paid ? tr("Payé") : tr("Impayé")}
            </span>
            {reg.order_id && (
              <span className="bg-slate-100 text-slate-700 uppercase tracking-widest px-2 py-1 border border-slate-200">
                Cmd · {reg.order_id.slice(0, 8)}
              </span>
            )}
            <span className="bg-slate-100 text-slate-700 uppercase tracking-widest px-2 py-1 border border-slate-200">
              {(reg.created_at || "").slice(0, 10)}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label={tr("Prénom")}            value={form.participant_first_name} onChange={(v) => set("participant_first_name", v)} testid="fiche-first_name" />
            <F label={tr("Nom")}               value={form.participant_last_name}  onChange={(v) => set("participant_last_name", v)}  testid="fiche-last_name" />
            <F label={tr("Date de naissance")} value={form.participant_birthdate}  onChange={(v) => set("participant_birthdate", v)}  type="date" testid="fiche-birthdate" />
            <F label="E-mail"            value={form.participant_email}      onChange={(v) => set("participant_email", v)}      type="email" testid="fiche-email" />
            <F label={tr("Téléphone")}         value={form.participant_phone}      onChange={(v) => set("participant_phone", v)}      type="tel" testid="fiche-phone" />
            <F label={tr("Adresse")}           value={form.participant_address}    onChange={(v) => set("participant_address", v)}    testid="fiche-address" />
            <F label="Poids (kg)"        value={form.participant_weight}     onChange={(v) => set("participant_weight", v)}     type="number" testid="fiche-weight" />
            <F label="Taille (cm)"       value={form.participant_height}     onChange={(v) => set("participant_height", v)}     type="number" testid="fiche-height" />
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Bateau souhaité")}</label>
              {canEdit ? (
                <select value={form.participant_boat} onChange={(e) => set("participant_boat", e.target.value)}
                  className="w-full border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-navy bg-white" data-testid="fiche-boat">
                  <option value="">—</option>
                  <option value="monoplace">Monoplace</option>
                  <option value="biplace">Biplace</option>
                </select>
              ) : (
                <div className="text-sm text-navy py-1.5">{form.participant_boat || "—"}</div>
              )}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Expérience / Notes")}</label>
            {canEdit ? (
              <textarea value={form.participant_experience} onChange={(e) => set("participant_experience", e.target.value)} rows={3}
                className="w-full border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-navy bg-white resize-none" data-testid="fiche-experience" />
            ) : (
              <div className="text-sm text-navy py-1.5 whitespace-pre-line">{form.participant_experience || "—"}</div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-sand flex flex-wrap items-center gap-2 sticky bottom-0">
          <button onClick={downloadPdf}
            className="px-4 py-2 text-xs uppercase tracking-widest border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
            data-testid="fiche-download-pdf">
            <FileText className="w-3 h-3 inline mr-1" /> Télécharger PDF
          </button>
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest text-slate-600 hover:text-navy" data-testid="fiche-cancel">
            Fermer
          </button>
          {canEdit && (
            <button disabled={saving} onClick={save}
              className="px-4 py-2 text-xs uppercase tracking-widest bg-navy text-white hover:bg-club-red disabled:opacity-50"
              data-testid="fiche-save">
              {saving ? tr("Enregistrement…") : tr("Enregistrer")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RegistrationsTable({ rows, canEditStatus, canTogglePay, canDelete, STATUS_OPTS, statusBadge, busy, updateStatus, togglePaid, del, exportCsv, onOpenFiche, downloadFichePdf }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-sand">
        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-600 border-b border-slate-200">
          <th className="px-3 py-3">Participant</th>
          <th className="px-3">Stage</th>
          <th className="px-3">{tr("Contact")}</th>
          <th className="px-3">{tr("Bateau · Poids · Taille")}</th>
          <th className="px-3">{tr("Statut")}</th>
          <th className="px-3">{tr("Paiement")}</th>
          <th className="px-3">{tr("Actions")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={7} className="text-center text-slate-500 py-6">{tr("Aucune inscription.")}</td></tr>
        )}
        {rows.map((r) => {
          const fullName = r.participant_first_name || r.participant_last_name
            ? `${r.participant_first_name || ""} ${r.participant_last_name || ""}`.trim()
            : r.participant_name || "—";
          return (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-sand/40" data-testid={`reg-row-${r.id}`}>
              <td className="px-3 py-3 align-top">
                <div className="font-serif text-navy">{fullName}</div>
                {r.participant_birthdate && (
                  <div className="text-[11px] text-slate-500 flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" /> {r.participant_birthdate}
                  </div>
                )}
              </td>
              <td className="px-3 align-top">
                <div className="text-navy text-sm">{r.stage_title || "—"}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{(r.created_at || "").slice(0, 10)}</div>
              </td>
              <td className="px-3 align-top text-[12px]">
                <div className="text-navy">{r.participant_email || r.parent_email || "—"}</div>
                <div className="text-slate-500">{r.participant_phone || r.parent_phone || ""}</div>
              </td>
              <td className="px-3 align-top text-[12px] text-slate-700">
                <div>{r.participant_boat || "—"}</div>
                <div className="text-slate-500">
                  {r.participant_weight ? `${r.participant_weight} kg` : "—"} · {r.participant_height ? `${r.participant_height} cm` : "—"}
                </div>
              </td>
              <td className="px-3 align-top">
                {canEditStatus ? (
                  <select
                    disabled={busy}
                    value={r.status || "pending"}
                    onChange={(e) => updateStatus(r, e.target.value)}
                    className={`text-xs uppercase tracking-widest border px-2 py-1 ${statusBadge(r.status)}`}
                    data-testid={`reg-status-${r.id}`}
                  >
                    {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <span className={`text-[11px] uppercase tracking-widest border px-2 py-1 inline-block ${statusBadge(r.status)}`}>
                    {r.status || "pending"}
                  </span>
                )}
              </td>
              <td className="px-3 align-top">
                {canTogglePay ? (
                  <button
                    disabled={busy}
                    onClick={() => togglePaid(r)}
                    className={`text-[11px] uppercase tracking-widest border px-2 py-1 ${r.paid ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}
                    data-testid={`reg-paid-${r.id}`}
                    title={tr("Basculer le statut de paiement")}
                  >
                    {r.paid ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3"/>{tr("Payé")}</span> : <span className="inline-flex items-center gap-1"><X className="w-3 h-3"/>{tr("Impayé")}</span>}
                  </button>
                ) : (
                  <span className={`text-[11px] uppercase tracking-widest border px-2 py-1 inline-block ${r.paid ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                    {r.paid ? tr("Payé") : tr("Impayé")}
                  </span>
                )}
              </td>
              <td className="px-3 align-top whitespace-nowrap">
                <button
                  onClick={() => onOpenFiche(r)}
                  className="text-[11px] uppercase tracking-widest text-navy border border-navy px-2 py-1 mr-1 hover:bg-navy hover:text-white"
                  data-testid={`reg-fiche-${r.id}`}
                  title={tr("Voir la fiche complète")}
                >
                  <Eye className="w-3 h-3 inline" />
                </button>
                <button
                  onClick={() => downloadFichePdf(r.id)}
                  className="text-[11px] uppercase tracking-widest text-emerald-700 border border-emerald-700 px-2 py-1 mr-1 hover:bg-emerald-700 hover:text-white"
                  data-testid={`reg-pdf-${r.id}`}
                  title={tr("Télécharger la fiche en PDF")}
                >
                  <FileText className="w-3 h-3 inline" />
                </button>
                {(canEditStatus || canDelete) && r.stage_id && (
                  <button
                    onClick={() => exportCsv(r.stage_id)}
                    className="text-[11px] uppercase tracking-widest text-navy border border-navy px-2 py-1 mr-1 hover:bg-navy hover:text-white"
                    data-testid={`reg-export-${r.id}`}
                    title={tr("Exporter la liste de ce stage en CSV")}
                  >
                    <FileDown className="w-3 h-3 inline" />
                  </button>
                )}
                {canDelete && (
                  <button
                    disabled={busy}
                    onClick={() => del(r)}
                    className="text-[11px] uppercase tracking-widest text-red-600 border border-red-600 px-2 py-1 hover:bg-red-600 hover:text-white"
                    data-testid={`reg-delete-${r.id}`}
                    title={tr("Supprimer l'inscription")}
                  >
                    <Trash2 className="w-3 h-3 inline" />
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
